/**
 * MarinaMapView receives space geometry data from the MarinaMapsAnnotationController and AssignmentsV2Controller and creates svg shapes based on that data. Then passes the shapes and map image to be rendered by the Canvas component.
 * @param {object} assignmentBoatImages - object of assignment boat image url strings.
 * @param {string} boatImage - url for boat image for space rectangle.
 * @param {boolean} editing - whether the controller is in editing state or not.
 * @param {boolean} editingExistingSpace - method to open editing sidebar when annotating.
 * @param {string} linearDockImage - url for dock image for linear spaces.
 * @param {boolean} loadingMap - shows loading screen if the spaceGeometry data and map image have not loading yet.
 * @param {method} loadReservationForSidebar - opens sidebar view with reservation information
 * @param {boolean} mapAssignmentView - determines if the spaceGeometry data is coming from the assignment controller
 * @param {string} mapId - The id for the map.
 * @param {string} mapImage - The url for the map image.
 * @param {boolean} performingBulkAction - whether the controller is performing a bulk action or not.
 * @param {object} selectedSpace - object of current selected space.
 * @param {object} selectedReservation - object of current selected reservation.
 * @param {string} selectedReservationId - object of selected reservationId, while the reservation is loading.
 * @param {method} setFormState - method that takes in an x and y param and sets formData state based on those values.
 * @param {object} spaceEditing - the space that is currently being edited.
 * @param {object} spaceGeometry - the current space geometry data from the space geometry form inputs.
 * @param {array} spacesWithGeometries - an array of objects that contain space and space geometry data previously saved to the marina map.
 */
import classNames from "classnames"
import ApplicationComponent from "legacy/ApplicationComponent"
import BoatSVGShape from "legacy/BoatSVGShape"
import SVGCanvas from "legacy/SvgCanvas"
import AssignmentSearchForm from "legacy/assignments-v2/AssignmentSearchForm"
import AssignmentsDateButtons from "legacy/assignments-v2/AssignmentsDateButtons"
import AssignmentsPopover from "legacy/assignments-v2/AssignmentsPopover"
import { hasAny, isEmpty } from "legacy/helpers.es6"
import PropTypes from "prop-types"
import React from "react"

class MarinaMapView extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.state = {
      popoverData: null,
      x: 0,
      y: 0,
      transformString: "",
      searchResults: [],
    }
    this.moveDateBackward = this.moveDateBackward.bind(this)
    this.moveDateForward = this.moveDateForward.bind(this)
    this.popoverDelayHandler = {}
    this.clearPopover = this.clearPopover.bind(this)
    this.setPopover = this.setPopover.bind(this)
    this.setGlobalState = this.setGlobalState.bind(this)
  }

  static get viewBoxDimensions() {
    return {
      x: 0,
      y: 0,
      height: 1900,
      width: 3000,
    }
  }

  setPopover(type, objects, spaceGroup) {
    // delay the popover so it doesn't flash as you move your cursor across the screen
    const firstObject = objects[0]
    const objectId =
      firstObject.type === "assignment"
        ? firstObject.reservation_id
        : firstObject.id

    this.popoverDelayHandler[`${type}-${objectId}`] = setTimeout(() => {
      this.setState({ popoverData: { type, data: { objects, spaceGroup } } })
    }, 150)
  }

  clearPopover(type, objects) {
    const firstObject = objects[0]
    const objectId =
      firstObject.type === "assignment"
        ? firstObject.reservation_id
        : firstObject.id

    clearTimeout(this.popoverDelayHandler[`${type}-${objectId}`])
    this.popoverDelayHandler[`${type}-${objectId}`] = null
    this.setState({ popoverData: null })
  }

  /** checks assignment data against reservation data to determine whether the assignment is the current one that is selected.
   * @param {object} assignment - assignment data that is being checked.
   * @returns {boolean}
   */
  assignmentClicked(assignment) {
    if (this.props.selectedReservationId) {
      return this.props.selectedReservationId === assignment.reservation_id
    } else if (this.props.selectedReservation) {
      return (
        this.props.selectedReservation.encoded_id === assignment.reservation_id
      )
    } else {
      return false
    }
  }

  moveDateBackward() {
    const date = this.props.date.clone().subtract(1, "days")
    this.props.dateChange(date)
  }

  moveDateForward() {
    const date = this.props.date.clone().add(1, "days")
    this.props.dateChange(date)
  }

  isLinearSpace(space) {
    return space.spaceGroup && space.spaceGroup.type === "SpaceGroup::LinearV2"
  }

  /**
   * @param {object} space - an object with space data.
   * @param {object} assignment - the specific assignment that the returned geometry data is related to.
   * @returns {object} - a space geometry object that is specific to an assignment on a linear space.
   */
  linearBoatGeometryObject(space, assignment) {
    const endOfDock = parseInt(space.spaceGroup.total)
    const rotation = parseInt(space.data.rotation)
    const spaceWidth = parseInt(space.data.width)
    const bowLocation = parseInt(assignment.bow_location)
    const sternLocation = parseInt(assignment.stern_location)
    const spaceX = parseInt(space.data.x)
    const tieSide = bowLocation > sternLocation ? "port" : "starboard"
    const boatRotation = tieSide === "port" ? rotation - 180 : rotation
    // sets the width of the boat image to be proportional to its length in comparison to the total dock length
    const boatImageWidth =
      (parseInt(assignment.length_overall) / endOfDock) * spaceWidth
    // the change in x is either the proportional location of the stern or bow to the end of the dock based on the tie side.
    const dX =
      tieSide === "port"
        ? (sternLocation / endOfDock) * spaceWidth
        : (bowLocation / endOfDock) * spaceWidth
    // the proportional distance of the bow or stern depending on the tieSide is added to the starting x coordinate of the parent space. The amount that is added is either the total width of the space minus dX or just dX depending on the tieSide.
    const x =
      tieSide === "port"
        ? spaceX + (spaceWidth - dX) - boatImageWidth
        : spaceX + dX
    return {
      ...space.data,
      x,
      width: boatImageWidth,
      rotation: boatRotation,
      centerX: parseInt(space.data.x) + parseInt(space.data.width / 2),
      centerY: parseInt(space.data.y) + parseInt(space.data.height / 2),
    }
  }

  /**
   * @param {object} assignments - list of assignments that the returned shape object is related to.
   * @returns {string} - a string that has shape class information related to the type of assignment.
   */
  assignmentShape(objects) {
    if (objects.length > 1) {
      const hasPlaceholders = objects.filter(
        (obj) => obj.type === "placeholder"
      )
      if (hasAny(hasPlaceholders)) {
        return "conflicting-with-placeholder"
      } else {
        return "conflicting-assignments"
      }
    } else {
      const object = objects[0]
      if (object.type === "placeholder") {
        return "placeholder"
      } else if (!object.checked_in_at) {
        return "confirmed-assignment"
      } else if (object.checked_in_at && !object.checked_out_at) {
        return "checked-in-assignment"
      } else {
        return "empty-space"
      }
    }
  }

  filterCheckedOutAssignments(objects) {
    if (!objects) return []
    return objects.filter((obj) => !obj.checked_out_at)
  }

  setGlobalState(object) {
    this.setState(object)
  }

  // Ensure we always have working required functions
  getSetPlaceholderFunction() {
    return (
      this.props.setPlaceholder ||
      (() => {
        console.warn(
          "setPlaceholder function was called but not provided to MarinaMapView"
        )
      })
    )
  }

  getLoadReservationFunction() {
    return (
      this.props.loadReservationForSidebar ||
      ((id, space) => {
        console.warn(
          "loadReservationForSidebar function was called but not provided to MarinaMapView"
        )
      })
    )
  }

  renderAnnotationRect() {
    if (this.props.editing) {
      if (this.props.performingBulkAction) {
        return (
          <BoatSVGShape
            geometry={this.props.spaceGeometry}
            shape={"bulk-action-rect"}
            centerX={
              parseInt(this.props.spaceGeometry.x) +
              parseInt(this.props.spaceGeometry.width / 2)
            }
            centerY={
              parseInt(this.props.spaceGeometry.y) +
              parseInt(this.props.spaceGeometry.height / 2)
            }
          />
        )
      } else {
        return (
          <BoatSVGShape
            geometry={this.props.spaceGeometry}
            shape={
              this.props.spaceEditing.spaceGroup &&
              this.props.spaceEditing.spaceGroup.type === "SpaceGroup::LinearV2"
                ? "linear-annotation-rect"
                : "annotation-rect"
            }
            boatImage={this.props.boatImage}
            linearDockImage={this.props.linearDockImage}
            centerX={
              parseInt(this.props.spaceGeometry.x) +
              parseInt(this.props.spaceGeometry.width / 2)
            }
            centerY={
              parseInt(this.props.spaceGeometry.y) +
              parseInt(this.props.spaceGeometry.height / 2)
            }
          />
        )
      }
    }
  }

  renderAll(space, index) {
    const hasAssignments = space.assignments ? 1 : 0
    const hasPlaceholders = space.placeholders ? 1 : 0
    const populatedTypes = hasAssignments + hasPlaceholders

    const assignments =
      this.filterCheckedOutAssignments(space.assignments) || []
    const hasCheckedOutAssignments = isEmpty(assignments)
    const placeholders = space.placeholders || []
    const allObjects = assignments.concat(...placeholders)

    // Use a consistent key - either the space ID or index, plus a type indicator
    // to ensure uniqueness across different rendering paths
    const spaceKey = space.id || space.encoded_id || `index-${index}`

    if (populatedTypes > 1) {
      return this.renderSpace(
        space,
        allObjects,
        space.spaceGroup,
        `multiple-${spaceKey}`
      )
    } else {
      if (hasPlaceholders > 0) {
        return this.renderSpace(
          space,
          placeholders,
          space.spaceGroup,
          `placeholder-${spaceKey}`
        )
      } else if (hasCheckedOutAssignments) {
        return this.renderEmptySpace(space, `empty-${spaceKey}`)
      } else {
        return this.renderSpace(
          space,
          assignments,
          space.spaceGroup,
          `assignment-${spaceKey}`
        )
      }
    }
  }

  renderSpacesWithGeometries() {
    if (this.props.mapAssignmentView) {
      return this.props.spacesWithGeometries.map((space, index) => {
        if (
          (space.assignments && hasAny(space.assignments)) ||
          (space.placeholders && hasAny(space.placeholders))
        ) {
          if (this.isLinearSpace(space)) {
            return (
              <React.Fragment key={`space-${space.id || index}`}>
                {space.assignments.map((assignment, assignmentIndex) =>
                  this.renderLinearDockageSpace(
                    space,
                    assignment,
                    space.spaceGroup
                  )
                )}
              </React.Fragment>
            )
          } else {
            return this.renderAll(space, index)
          }
        } else {
          return this.renderEmptySpace(space, index)
        }
      })
    } else {
      return this.props.spacesWithGeometries.map((space, index) =>
        this.renderAnnotationSpace(space, index)
      )
    }
  }

  renderLinearDockageSpace(space, assignment, spaceGroup) {
    // Create a stable, unique key for this linear dockage space assignment
    const key =
      assignment.reservation_id ||
      `linear-${space.id || ""}-${assignment.id || ""}-${Math.random()
        .toString(36)
        .substr(2, 9)}`

    return (
      <BoatSVGShape
        key={key}
        geometry={this.linearBoatGeometryObject(space, assignment)}
        shape={this.assignmentShape([assignment])}
        centerX={this.linearBoatGeometryObject(space, assignment).centerX}
        centerY={this.linearBoatGeometryObject(space, assignment).centerY}
        setPopover={() =>
          this.setPopover("assignment", [assignment], spaceGroup)
        }
        clearPopover={() =>
          this.clearPopover("assignment", [assignment], spaceGroup)
        }
        assignmentClicked={this.assignmentClicked(assignment)}
        reservationLabel={assignment.reservationLabel}
        reservationLabelsEnabled={this.props.reservationLabelsEnabled}
        onClick={this.props.handleMarinaMapSpaceClick.bind(this, space, [
          assignment,
        ])}
      />
    )
  }

  renderSpace(space, objects, spaceGroup, customKey) {
    if (hasAny(objects)) {
      // Use a custom key if provided, otherwise generate one based on object properties
      const key =
        customKey ||
        (objects[0].type === "assignment"
          ? `assignment-${objects[0].reservation_id}`
          : `object-${
              objects[0].id || Math.random().toString(36).substr(2, 9)
            }`)

      return (
        <BoatSVGShape
          key={key}
          geometry={space.data}
          innerText={space.name}
          shape={this.assignmentShape(objects)}
          centerX={parseInt(space.data.x) + parseInt(space.data.width / 2)}
          centerY={parseInt(space.data.y) + parseInt(space.data.height / 2)}
          setPopover={() =>
            this.setPopover(objects[0].type, objects, spaceGroup)
          }
          clearPopover={() =>
            this.clearPopover(objects[0].type, objects, spaceGroup)
          }
          assignmentClicked={
            this.props.selectedSpace
              ? this.props.selectedSpace.name === space.name
              : false
          }
          reservationLabel={
            objects.length === 1 ? objects[0].reservationLabel : null
          }
          reservationLabelsEnabled={this.props.reservationLabelsEnabled}
          onClick={this.props.handleMarinaMapSpaceClick.bind(
            this,
            space,
            objects
          )}
        />
      )
    }
    return null // Make sure to return null when no objects are present
  }

  renderEmptySpace(space, index) {
    return (
      <BoatSVGShape
        key={index}
        geometry={space.data}
        innerText={this.isLinearSpace(space) ? "" : space.name}
        shape={this.isLinearSpace(space) ? "empty-linear-space" : "empty-space"}
        centerX={parseInt(space.data.x) + parseInt(space.data.width / 2)}
        centerY={parseInt(space.data.y) + parseInt(space.data.height / 2)}
      />
    )
  }

  renderAnnotationSpace(space, index) {
    return (
      <BoatSVGShape
        key={index}
        geometry={space.data}
        innerText={space.name}
        shape={
          this.isLinearSpace(space) ? "linear-space-set-rect" : "map-set-rect"
        }
        centerX={parseInt(space.data.x) + parseInt(space.data.width / 2)}
        centerY={parseInt(space.data.y) + parseInt(space.data.height / 2)}
        onClick={() => {
          return !this.props.performingBulkAction
            ? this.props.editingExistingSpace(space)
            : null
        }}
      />
    )
  }

  renderHighlightedSpace(spaceData, index) {
    const x = parseInt(spaceData.x)
    const y = parseInt(spaceData.y)
    const width = parseInt(spaceData.width)
    const height = parseInt(spaceData.height)
    return (
      <rect
        key={index}
        x={x - 10}
        y={y - 10}
        width={width + 20}
        height={height + 20}
        rx="10"
        ry="10"
        transform={`rotate(${spaceData.rotation}, ${x + width / 2}, ${
          y + height / 2
        })`}
      />
    )
  }

  renderHighlightedSearchResults() {
    const { viewBoxDimensions } = MarinaMapView
    const { searchResults, highlightedSpace } = this.state
    return (
      <svg
        transform={this.state.transformString}
        viewBox={`${viewBoxDimensions.x} ${viewBoxDimensions.y} ${viewBoxDimensions.width} ${viewBoxDimensions.height}`}
        className={classNames(
          "pointer-events-none absolute bottom-0 left-0 right-0 top-0",
          {
            block: hasAny(searchResults),
            hidden: isEmpty(searchResults),
          }
        )}
      >
        <mask id="excludeRects">
          <rect fill="white" x="0" y="0" width="100%" height="100%" />
          {highlightedSpace
            ? this.renderHighlightedSpace(highlightedSpace.data)
            : searchResults.map((searchResult, index) => {
                return this.renderHighlightedSpace(searchResult.data, index)
              })}
        </mask>
        <rect
          fill="gray"
          opacity="0.5"
          x="0"
          y="0"
          width="100%"
          height="100%"
          mask="url(#excludeRects)"
        />
      </svg>
    )
  }

  renderHeader() {
    if (this.props.mapAssignmentView) {
      return (
        <div className="flex w-full justify-between rounded-t border border-b-0 bg-white p-2">
          <div className="flex">
            <AssignmentsDateButtons
              dayIncrement={1}
              moveBackward={this.moveDateBackward}
              moveForward={this.moveDateForward}
              visibleRange={this.props.visibleRange}
            />
          </div>
          <div className="flex w-2/5">
            <AssignmentSearchForm
              searchFormData={this.props.searchFormData}
              setGlobalState={this.setGlobalState}
              handleMarinaMapSpaceClick={this.props.handleMarinaMapSpaceClick}
              loadReservationForSidebar={this.getLoadReservationFunction()}
              setPlaceholder={this.getSetPlaceholderFunction()}
              viewType={this.props.viewType}
            />
          </div>
        </div>
      )
    }
  }

  renderLoadingScreen() {
    return (
      <div className="card">
        <div className="flex h-screen flex-col items-center justify-center">
          <i className="text-muted fa-kit fa-spinner fa-spin" />
          <span>Loading Map...</span>
        </div>
      </div>
    )
  }

  renderMap() {
    return (
      <div className="w-full">
        {this.renderHeader()}
        <SVGCanvas
          backgroundImage={this.props.mapImage}
          hasHeader={this.props.mapAssignmentView}
          id={`map:${this.props.mapId}`}
          setZoomFromCookie={!!this.props.mapAssignmentView}
          viewBoxWidth={MarinaMapView.viewBoxDimensions.width}
          viewBoxHeight={MarinaMapView.viewBoxDimensions.height}
          viewBoxX={MarinaMapView.viewBoxDimensions.x}
          viewBoxY={MarinaMapView.viewBoxDimensions.y}
          editing={this.props.editing}
          performingBulkAction={this.props.performingBulkAction}
          setFormState={this.props.setFormState}
          setGlobalState={this.setGlobalState}
        >
          {this.renderSpacesWithGeometries()}
          {this.renderHighlightedSearchResults()}
          {this.renderAnnotationRect()}
        </SVGCanvas>
        <AssignmentsPopover data={this.state.popoverData} />
      </div>
    )
  }

  render() {
    return this.props.loadingMap ? this.renderLoadingScreen() : this.renderMap()
  }
}

MarinaMapView.propTypes = {
  assignmentBoatImages: PropTypes.object,
  boatImage: PropTypes.string,
  date: PropTypes.object,
  dateChange: PropTypes.func,
  editing: PropTypes.bool,
  editingExistingSpace: PropTypes.func,
  handleMarinaMapSpaceClick: PropTypes.func,
  linearDockImage: PropTypes.string,
  loadingMap: PropTypes.bool,
  loadReservationForSidebar: PropTypes.func,
  mapAssignmentView: PropTypes.bool,
  mapId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mapImage: PropTypes.string,
  performingBulkAction: PropTypes.bool,
  reservationLabelsEnabled: PropTypes.bool,
  searchFormData: PropTypes.array,
  selectedReservation: PropTypes.object,
  selectedReservationId: PropTypes.string,
  selectedSpace: PropTypes.object,
  setFormState: PropTypes.func,
  setPlaceholder: PropTypes.func,
  spaceEditing: PropTypes.object,
  spaceGeometry: PropTypes.object,
  spacesWithGeometries: PropTypes.array,
  viewType: PropTypes.string,
  visibleRange: PropTypes.object,
}

export default MarinaMapView
